import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import { useUser } from "@/web/auth/useUser";
import { Button } from "@/web/components/primitives/button";
import { Card, CardContent } from "@/web/components/primitives/card";
import { toaster } from "@/web/components/primitives/toast";
import { DefaultNavbar } from "@/web/components/styled/AppNavbar";
import { ErrorHandler } from "@/web/lib/errors";
import { tableQueryOptions, useProcedureMutation } from "@/web/lib/tanstack";
import { procedures, tables } from "@tycho/web-bindings";
import {
  aboutYouDataFromUserInfo,
  AboutYouForm,
  buildPartialAboutYouUserInfoMutationArgs,
  useAboutYouForm,
} from "@/web/routes/_protected/smb/-onboarding/AboutYouForm";

import { AvatarPicker, type AvatarState } from "@/web/routes/_protected/profile/-AvatarPicker";
import { BrandsSection } from "@/web/routes/_protected/profile/-BrandsSection";

export const Route = createFileRoute("/_protected/profile/user")({
  component: UserProfilePage,
});

function UserProfilePage() {
  const user = useUser();
  const { data: userInfoRows } = useSuspenseQuery(tableQueryOptions(tables.currentUserInfo));
  const userInfo = userInfoRows[0];

  const { mutateAsync: upsertUserInfo, isPending: isSaving } = useProcedureMutation(
    procedures.upsertUserInfo,
  );

  // useSuspenseQuery guarantees the row is loaded before this runs, so the form
  // initializes with stored values (or empty defaults when none exists yet).
  const prefill = userInfo ? aboutYouDataFromUserInfo(userInfo, user?.email ?? "") : undefined;
  const { aboutYouForm: form, aboutYouData } = useAboutYouForm({
    defaultEmail: user?.email,
    prefill,
  });

  const [avatarState, setAvatarState] = useState<AvatarState>({
    kind: "stored",
    path: userInfo?.avatarCdnPath,
  });

  async function handleSave() {
    const submitArgs = buildPartialAboutYouUserInfoMutationArgs(aboutYouData);

    const avatarCdnPath =
      avatarState.kind === "pending"
        ? avatarState.path
        : avatarState.kind === "cleared"
          ? ""
          : (avatarState.path ?? undefined);

    try {
      await upsertUserInfo({ ...submitArgs, avatarCdnPath });
      toaster({ message: "Profile saved", variant: "success" });
      // Reset RHF's baseline so isDirty goes back to false.
      form.reset(aboutYouData);
      // Reflect the persisted avatar so a subsequent "Remove" toggle stays accurate.
      setAvatarState({
        kind: "stored",
        path:
          avatarState.kind === "pending"
            ? avatarState.path
            : avatarState.kind === "cleared"
              ? undefined
              : avatarState.path,
      });
    } catch (error) {
      ErrorHandler.default(error);
    }
  }

  const isFormDirty = form.formState.isDirty;
  const isAvatarDirty = avatarState.kind !== "stored";
  const canSave = !isSaving && (isFormDirty || isAvatarDirty);

  return (
    <div className="flex min-h-dvh flex-col">
      <DefaultNavbar />
      <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col gap-6 px-4 pt-6 pb-20 md:px-8">
        <header className="flex flex-col gap-1">
          <h1 className="withus-strong text-2xl">Your profile</h1>
          <p className="text-sm text-muted-foreground">
            Keep your contact details and avatar up to date.
          </p>
        </header>

        <Card className="border-black/10">
          <CardContent className="flex flex-col gap-2 p-4">
            <h2 className="text-sm font-medium">Avatar</h2>
            <AvatarPicker email={user?.email ?? ""} state={avatarState} onChange={setAvatarState} />
          </CardContent>
        </Card>

        <AboutYouForm form={form} />

        <div className="flex justify-end">
          <Button type="button" disabled={!canSave} onClick={() => void handleSave()}>
            {isSaving ? "Saving…" : "Save changes"}
          </Button>
        </div>

        <BrandsSection />
      </div>
    </div>
  );
}
